import React from "react";
import { SvgIcon } from "../../components/SvgComponents";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";


interface GeometricLiquidInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const GeometricLiquidInsights: React.FC<GeometricLiquidInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  const entrance = spring({
    frame: elapsedFrames,
    fps,
    config: { damping: 14, mass: 0.5, stiffness: 120 },
  });

  const opacity = interpolate(elapsedFrames, [0, 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const floatY = Math.sin(globalFrame * 0.05) * 10;

  const words = activeBullet.text.split(" ");
  const wordDelay = 1.5;

  const isHighlighted = (word: string) => {
    if (!activeBullet.highlights) return false;
    const cleanWord = word.toLowerCase().replace(/[^a-z0-9%$-]/g, "");
    return activeBullet.highlights.some((h) => {
      const cleanHighlight = h.toLowerCase().replace(/[^a-z0-9%$-]/g, "");
      return cleanHighlight.includes(cleanWord) || cleanWord.includes(cleanHighlight);
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        flex: 1,
        justifyContent: "center",
        gap: "40px",
        padding: "0 40px",
      }}
    >
      {/* 1. Technical SVG Visualizer */}
      <div
        style={{
          width: "100%",
          height: "300px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          opacity,
          transform: `scale(${entrance}) translateY(${floatY}px)`,
        }}
      >
        <div
          style={{
            position: "relative",
            width: "300px",
            height: "300px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Decorative rotating ring */}
          <div
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              border: "2px dashed rgba(34, 211, 238, 0.4)",
              borderRadius: "50%",
              transform: `rotate(${globalFrame * 0.5}deg)`,
            }}
          />

          <div
            style={{
              filter: "drop-shadow(0 0 20px rgba(190, 242, 100, 0.4))",
            }}
          >
            {(() => {
              const brand = detectBrandInText(activeBullet.text);
              return brand ? (
                <CompanyBadge
                  name={brand}
                  color="#bef264"
                  accentColor="#bef264"
                  size={150}
                  style="minimal"
                />
              ) : (
                <SvgIcon
                  name={activeBullet.icon || "sparkles"}
                  width={240}
                  height={240}
                  primaryColor="#bef264"
                />
              );
            })()}
          </div>
        </div>
      </div>

      {/* 2. Insight Label */}
      <div
        style={{
          padding: "6px 16px",
          borderRadius: "4px",
          border: "1px solid #22d3ee",
          background: "rgba(34, 211, 238, 0.1)",
          color: "#22d3ee",
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 700,
          fontSize: "12px",
          letterSpacing: "2px",
          textTransform: "uppercase",
          opacity,
          transform: `translateX(${interpolate(entrance, [0, 1], [-20, 0])}px)`,
        }}
      >
        Insight_0{activeIndex + 1}
      </div>

      {/* 3. Staggered Word Display */}
      <div
        style={{
          width: "100%",
          minHeight: "200px",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignContent: "center",
        }}
      >
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "40px",
            fontWeight: 600,
            lineHeight: 1.4,
            color: "#ffffff",
            textAlign: "center",
            margin: 0,
          }}
        >
          {words.map((word, index) => {
            const wordStartFrame = bulletStartFrame + index * wordDelay;
            const wordElapsed = Math.max(0, globalFrame - wordStartFrame);

            const wordScale = spring({
              frame: wordElapsed,
              fps,
              config: { damping: 12, mass: 0.5, stiffness: 140 },
            });

            const wordOpacity = interpolate(wordElapsed, [0, 6], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });

            const highlighted = isHighlighted(word);

            return (
              <span
                key={index}
                style={{
                  display: "inline-block",
                  marginRight: "10px",
                  opacity: wordOpacity,
                  transform: `scale(${wordScale})`,
                }}
              >
                {highlighted ? (
                  <strong
                    style={{
                      fontWeight: 700,
                      color: "#bef264", // Electric Lime
                      textShadow: "0 0 15px rgba(190, 242, 100, 0.5)",
                    }}
                  >
                    {word}
                  </strong>
                ) : (
                  <span>{word}</span>
                )}
              </span>
            );
          })}
        </p>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
